/**
 * Where a floating box (a popover, a menu) goes, given the thing it hangs off.
 *
 * This replaces the positioning engine Radix brought with it. The box is in the top layer and
 * `position: fixed`, so every number here is in viewport coordinates and nothing about the page's
 * scroll containers or transforms has to be known.
 *
 * The rules are the ones the old API promised:
 *
 * - `side` is which edge of the anchor the box sits against, `sideOffset` the gap between them.
 * - `align` lines the box up with the start, centre or end of that edge, `alignOffset` nudges it.
 * - When the box would run off the viewport on its side and the opposite side has more room, it
 *   FLIPS. A menu opened at the bottom of the screen opens upwards instead of under the fold.
 * - Along the edge it slides back inside the viewport (keeping `collisionPadding` clear), but never
 *   so far that it stops touching the anchor.
 */

export type Side = 'top' | 'right' | 'bottom' | 'left';
export type Align = 'start' | 'center' | 'end';

export interface RectLike {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface Size {
  width: number;
  height: number;
}

export interface PlacementOptions {
  side?: Side;
  align?: Align;
  sideOffset?: number;
  alignOffset?: number;
  /** Flip and slide to stay on screen. On by default, as it was. */
  avoidCollisions?: boolean;
  /** Space to keep clear between the box and the viewport's edges. */
  collisionPadding?: number | Partial<Record<Side, number>>;
}

export interface Placement {
  top: number;
  left: number;
  /** The side actually used, after any flip. */
  side: Side;
  align: Align;
}

const OPPOSITE: Record<Side, Side> = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };

function paddingOf(padding: PlacementOptions['collisionPadding']): Record<Side, number> {
  if (typeof padding === 'number') {
    return { top: padding, right: padding, bottom: padding, left: padding };
  }
  return {
    top: padding?.top ?? 0,
    right: padding?.right ?? 0,
    bottom: padding?.bottom ?? 0,
    left: padding?.left ?? 0,
  };
}

/** How far past the padded viewport edge the box would reach on `side`. 0 when it fits. */
function overflowOn(
  side: Side,
  anchor: RectLike,
  box: Size,
  viewport: Size,
  offset: number,
  pad: Record<Side, number>,
): number {
  switch (side) {
    case 'bottom':
      return Math.max(
        0,
        anchor.top + anchor.height + offset + box.height - (viewport.height - pad.bottom),
      );
    case 'top':
      return Math.max(0, pad.top - (anchor.top - offset - box.height));
    case 'right':
      return Math.max(
        0,
        anchor.left + anchor.width + offset + box.width - (viewport.width - pad.right),
      );
    case 'left':
      return Math.max(0, pad.left - (anchor.left - offset - box.width));
  }
}

function aligned(start: number, length: number, boxLength: number, align: Align, offset: number) {
  if (align === 'start') return start + offset;
  if (align === 'end') return start + length - boxLength - offset;
  return start + (length - boxLength) / 2 + offset;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

export function computePosition(
  anchor: RectLike,
  box: Size,
  viewport: Size,
  options: PlacementOptions = {},
): Placement {
  const {
    side: preferred = 'bottom',
    align = 'center',
    sideOffset = 0,
    alignOffset = 0,
    avoidCollisions = true,
    collisionPadding = 0,
  } = options;
  const pad = paddingOf(collisionPadding);

  let side = preferred;
  if (avoidCollisions) {
    const here = overflowOn(preferred, anchor, box, viewport, sideOffset, pad);
    if (here > 0) {
      const opposite = OPPOSITE[preferred];
      // Flip only when it helps: a box too big for either side stays where it was asked to be
      // unless the other side cuts less of it off.
      if (overflowOn(opposite, anchor, box, viewport, sideOffset, pad) < here) side = opposite;
    }
  }

  let top: number;
  let left: number;
  if (side === 'top' || side === 'bottom') {
    top =
      side === 'bottom'
        ? anchor.top + anchor.height + sideOffset
        : anchor.top - sideOffset - box.height;
    left = aligned(anchor.left, anchor.width, box.width, align, alignOffset);
    if (avoidCollisions) {
      left = clamp(left, pad.left, viewport.width - pad.right - box.width);
      // Never slide clear of the anchor: the box must still visibly belong to it.
      left = clamp(left, anchor.left - box.width, anchor.left + anchor.width);
    }
  } else {
    left =
      side === 'right'
        ? anchor.left + anchor.width + sideOffset
        : anchor.left - sideOffset - box.width;
    top = aligned(anchor.top, anchor.height, box.height, align, alignOffset);
    if (avoidCollisions) {
      top = clamp(top, pad.top, viewport.height - pad.bottom - box.height);
      top = clamp(top, anchor.top - box.height, anchor.top + anchor.height);
    }
  }

  return { top: Math.round(top), left: Math.round(left), side, align };
}

/** The viewport as `position: fixed` sees it, without the scrollbar. */
export function viewportSize(): Size {
  const root = document.documentElement;
  return {
    width: root.clientWidth || window.innerWidth,
    height: root.clientHeight || window.innerHeight,
  };
}

/**
 * Calls `update` whenever the box may have come adrift from its anchor: any scroll anywhere on
 * the page (captured, so scrolling a table inside a card counts), a window resize, or either
 * element changing size. Returns the function that stops listening.
 */
export function autoUpdate(anchor: Element, box: Element, update: () => void): () => void {
  const options = { capture: true, passive: true } as const;
  window.addEventListener('scroll', update, options);
  window.addEventListener('resize', update);
  const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(() => update()) : null;
  observer?.observe(anchor);
  observer?.observe(box);
  return () => {
    window.removeEventListener('scroll', update, options);
    window.removeEventListener('resize', update);
    observer?.disconnect();
  };
}
